import { ActionContext, Mutation } from 'vuex';

type Prefixed<K extends string, T> = {
  [P in keyof T as P extends string ? `${K}${P}` : never]: T[P];
}

type Suffixed<K extends string, T> = {
  [P in keyof T as P extends string ? `${P}${K}` : never]: T[P];
}

type ResourceModule<K extends string, T> = {
  state: Prefixed<Lowercase<K>, {
    '': T,
    Loading: boolean;
    Loaded: boolean;
  }>;
  mutations: Prefixed<K, {
    '': Mutation<any>;
    _LOADING: Mutation<any>;
    _LOADED: Mutation<any>;
  }>;
  actions: Suffixed<Capitalize<Lowercase<K>>, {
    get: (context: any) => Promise<void>;
    load: (context: any) => Promise<T>;
  }>
}

/** Create state with an action that fetches data only if isnt fetched yet */
export default function createResource<T, K extends string>(key: K, fetcher: () => Promise<T>, defaultValue: T): ResourceModule<K, T> {
  const SET = `SET_${key}` as const;
  const SET_LOADING = `SET_${key}_LOADING` as const;
  const SET_LOADED = `SET_${key}_LOADED` as const;
  const title = capitalize(key) as Capitalize<Lowercase<K>>;
  const STATE = key.toLowerCase() as Lowercase<K>;
  const STATE_LOADING = `${STATE}Loading` as const;
  const STATE_LOADED = `${STATE}Loaded` as const;
  const ACTION_LOAD = `load${title}` as const;
  const ACTION_GET = `get${title}` as const;
  return {
    state: {
      [STATE]: defaultValue,
      [STATE_LOADING]: false,
      [STATE_LOADED]: false,
    },
    mutations: {
      [SET]: (state, payload) => {
        state[STATE] = payload;
      },
      [SET_LOADING]: (state, payload) => {
        state[STATE_LOADING] = payload;
      },
      [SET_LOADED]: (state, payload) => {
        state[STATE_LOADED] = payload;
      },
    },
    actions: {
      [ACTION_LOAD]: (async ({ commit }: ActionContext<{}, {}>) => {
        commit(SET_LOADING, true);
        try {
          const data = await fetcher();
          commit(SET, data);
          commit(SET_LOADED, true);
        } catch (error) {
          console.error(`Failed to fetch "${key}":`, error);
        } finally {
          commit(SET_LOADING, false);
        }
      }),
      [ACTION_GET]: ({ state, dispatch }: ActionContext<{
        [STATE]: T;
        [STATE_LOADING]: boolean;
        [STATE_LOADED]: boolean;
      }, {}>) => {
        if (!state[STATE_LOADED] && !state[STATE_LOADING]) {
          dispatch(ACTION_LOAD);
        }
        return state[STATE];
      },
    },
  } as ResourceModule<K, T>;
}

function capitalize(val: string) {
  return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
}
